import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, delay, map, retry, retryWhen, shareReplay, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import Sensor from '../models/sensor';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class SensorsService {
  private paramsObject: any;
  private param: string;
  private base_url: string = environment.base_url;
  public unAssignedSensors: BehaviorSubject<any[]> = new BehaviorSubject([]);
  public allSensors: BehaviorSubject<any[]> = new BehaviorSubject([]);

  constructor(private http: HttpClient) { }

  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error('An error occurred:', error.error.message || error.message);
    return throwError('Something bad happened; please try again later.');
  }

  /**
   * Get all Sensors
   * @param none
   * @returns sensors : Sensor[]
   */
  getAll() {
    return this.http.get(`${this.base_url}/api/sensor?active=True`)
      .pipe(
        retry(2),
        map(res => {
          if (res) return res
        }),
        catchError(err => {
          return throwError(err)
        })
      )
  }

  /**
   * Get Sensor by ID
   * @param id
   * @type number
   * @returns sensors: Sensor[]
   */
  getSensorById(id?: number, nullGroup?: boolean): any {
    let url = `${this.base_url}/api/sensor`

    if (id != null && !nullGroup) {
      url += `?sensor=${id}`
    } else {
      url += `?active=True&group=null`
    }
    return this.http.get(url)
      .pipe(
        retry(2),
        map(res => {
          if (res) return res
        }),
        catchError(err => {
          return throwError(err)
        })
      )
  }

  /**
   * GET Sensor Real Values by serial
   * @param serial
   * @type number
   * @returns sensor : any[]
   */
  getRealValuesById(serial: number): any {
    return this.http.get(`${this.base_url}/api/realvalues?sensor=${serial}`)
      .pipe(
        retry(2),
        map(res => {
          if (res) return res
        }),
        catchError(err => {
          return throwError(err)
        })
      )
  }

  /**
   * GET Group Sensors by ID
   * @param id
   * @type number
   * @returns sensors: Sensor[]
   */
  getGroupById(id: number): any {
    return this.http.get(`${this.base_url}/api/sensor?group=${id}`)
      .pipe(
        retry(2),
        map(res => {
          if (res) return res
        }),
        catchError(err => {
          return throwError(err)
        })
      )
  }

  /**
   *
   * @param groupsForRules
   * if you pass false or nothing then we transform the data
   * @returns {
   *  name: string,
   *  id: string,
   *  sensors: number
   * }
   *
   * where sensors is the sensors Length
   * if you pass true we return the response as is
   */
  getGroups(groupsForRules?: boolean) {
    return this.http.get(`${this.base_url}/api/groups`)
      .pipe(
        retry(2),
        map(res => {
          return res['data'].length ? res['data'] : []
        }),
        map(res => {
          if (!groupsForRules || groupsForRules === undefined) {
            let result = res.map(item => {
              return {
                name: item['attributes']['name'],
                id: item['id'],
                sensors: item['attributes']['sensors'].length
              }
            })
            return result
          } else return res
        }),
        shareReplay(),
        catchError(err => throwError(err))
      )
  }

  /**
   * Delete Group by ID
   * @param group
   * @type object
   * @returns Group: Group
   */
  deleteGroup(group): Observable<any> {
    return this.http.delete(`${this.base_url}/api/groups/${group.id}`).pipe(
      retryWhen(errors => errors.pipe(
        delay(3000),
        take(2)
      )),
      catchError((error: HttpErrorResponse) => {
        return throwError(this.handleError);
      })
    );
  }

  /**
   * POST Group Sensors by ID
   * @param sensor
   * @type object
   * @returns sensor: Sensor
   */
  createSensor(sensor: Sensor): Observable<any> {
    return this.http.post(`${this.base_url}/api/sensor`, sensor).pipe(
      retry(2),
      catchError((err: HttpErrorResponse) => {
        return throwError(this.handleError);
      })
    );
  }
  
  /**
   * PUT Group Sensors by ID
   * @param sensor
   * @type object
   * @returns sensor: Sensor
   */
  updateSensor(sensor) {
    return this.http.put(`${this.base_url}/api/sensor/${Number(sensor.data.id)}`, sensor)
      .pipe(
        retry(2),
        map(sensor => {
          if (sensor) return sensor;

        }),
        catchError(err => {
          const errors = [...err['error']['errors']]
          return throwError(errors);
        })
      )
  }

  /**
   * PATCH Group Sensors by ID
   * @param sensor
   * @type object
   * @returns sensor: Sensor
   */
  patchSensor(sensor) {
    return this.http.put(`${this.base_url}/api/sensor/${sensor.sensorID}`, sensor)
      .pipe(
        retry(2),
        map(result => {
          if (result) return result;

        }),
        catchError(err => {
          const errors = [...err['error']['errors']]
          return throwError(errors);
        })
      )
  }


  /**
   * DELETE sensor by ID
   * @param id
   * @type number
   * @returns void
   */

  deleteSensor(sensorID: number): Observable<any> {
    return this.http.delete(`${this.base_url}/api/sensor/${sensorID}`).pipe(
      retry(2),
      map(response => response),
      catchError(this.handleError)
    );
  }
  transformSensors(sensors: any[]) {
    let sensorsArray: any[] = []

    sensorsArray = sensors.map(sensor => {
      return {
        serial: sensor['attributes']['serial'],
        id: sensor['id']
      }
    })
    this.allSensors.next(sensorsArray)
  }

  transformSensorsUnAssigned(sensors: any[]) {
    let sensorsAssignedArray: any[] = []

    sensorsAssignedArray = sensors.map(sensor => {
      return {
        serial: sensor['attributes']['serial'],
        id: sensor['id']
      }
    })

    this.unAssignedSensors.next(sensorsAssignedArray)
  }

  // arrayDiffByKey(arrayOne: any[], arrayTwo: any[]): any[] {
  // return arrayOne.filter(({ serial: id1 }) => !arrayTwo.some(({ serial: id2 }) => id2 === id1));
  // }

  arrayDiffByKey(b1, b2): any[] {
    return [b1, b2].sort((a, b) => b.length - a.length)
      .reduce((a, b) => a.filter(o => !b.some(v => v.id === o.id)));
  }

  /**
   * @returns all sensors array
   */
  public getSensorsArray() {
    console.log(`All Sensors`, this.allSensors.value)
    return this.allSensors.value
  }

  /**
   * @returns all assigned sensors array
   */
  public getSensorsUnAssignedArray() {
    console.log(`UnAssignedSensors`, this.unAssignedSensors.value)
    return this.unAssignedSensors.value
  }

}
